// Shared offer-combination-policy helper for the billing payment side panel.
//
// `loyalty_config.redeem_config.benefit_policy` decides how many of the three
// discount sources the cashier can stack on a single bill:
//
//   'one' → exactly ONE of {points, coupon, scheme}
//   'two' → at most TWO of the three
//   'all' → all three may stack
//
// Each panel (LoyaltyPanel / CouponPanel / SchemePanel) calls
// `canAdd(kind, values, policy)` before applying its own discount. The
// function counts currently-active sources from Formik state and rejects
// the (N+1)th attempt with a user-facing reason.
//
// We treat each "kind" as a singleton class — a bill can hold AT MOST one
// applied coupon, one selected scheme, one points redemption. So the count
// is just a tri-bit set.

export const POLICY_LIMITS = {
  one: 1,
  two: 2,
  all: 3,
};

const POLICY_LABEL = {
  one: "Only one discount type may apply per bill",
  two: "At most two discount types may apply per bill",
  all: "All discount types may stack",
};

// Bit-set of which sources are currently active on the bill (read from
// Formik). Each panel writes a discriminator field when it applies — we look
// for those.
export function activeSources(values) {
  // points are now stored as a discount line (values.pointsDiscount) — same
  // shape as scheme/coupon. The legacy paymentData "Points" entry is kept as
  // a fallback for in-flight bills started before the redeem-as-discount
  // switchover.
  const points =
    !!values?.pointsDiscount ||
    !!(
      values?.paymentData &&
      values.paymentData.some((p) => p?.payment_mode_name === "Points")
    );
  const coupon = !!values?.couponDiscount;
  const scheme = !!values?.schemeDiscount;
  return { points, coupon, scheme };
}

export function activeCount(values) {
  const a = activeSources(values);
  return (a.points ? 1 : 0) + (a.coupon ? 1 : 0) + (a.scheme ? 1 : 0);
}

// Resolve the active policy from a member object (preferred — the loyalty
// API attaches it) or from a runtime config snapshot if provided. Defaults
// to 'two' (matches the migration's seed default) when neither is present
// so a missing config doesn't accidentally hard-block the cashier.
export function resolvePolicy(member, fallback = "two") {
  const p = member?.redeem_config?.benefit_policy || fallback;
  return p === "one" || p === "two" || p === "all" ? p : "two";
}

// Caller passes the KIND it wants to add ('points' | 'coupon' | 'scheme')
// plus current Formik values + the resolved policy. Returns:
//   { ok: true } when the add is allowed (either the kind is already
//     active — replacing in place — or we're below the limit)
//   { ok: false, reason: "..." } otherwise
export function canAdd(kind, values, policy) {
  const limit = POLICY_LIMITS[policy] ?? 3;
  const a = activeSources(values);
  // Replacing the same kind always allowed (it doesn't grow the count).
  if (a[kind]) return { ok: true };
  const count = (a.points ? 1 : 0) + (a.coupon ? 1 : 0) + (a.scheme ? 1 : 0);
  if (count < limit) return { ok: true };
  const activeNames = [
    a.points && "Points",
    a.coupon && "Coupon",
    a.scheme && "Scheme",
  ].filter(Boolean).join(" + ");
  return {
    ok: false,
    reason: `${POLICY_LABEL[policy] || POLICY_LABEL.two}. Active: ${activeNames}. Remove one before applying ${labelFor(kind)}.`,
  };
}

function labelFor(kind) {
  if (kind === "points") return "loyalty points";
  if (kind === "coupon") return "a coupon";
  if (kind === "scheme") return "a scheme";
  return kind;
}
